/* eslint-disable no-console */
import types from '../mutation-types';
import PipelineAPI from '../../api/pipelines';
import { handlePipelineError } from '../../routes/dashboard/pipeline/helpers/pipelineErrorHandler';

const state = {
  records: [],
  selectedPipeline: null,
  isPipelineEnabled: false,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

const getters = {
  getPipelines: $state => $state.records,
  getSelectedPipeline: $state => $state.selectedPipeline,
  getUIFlags: $state => $state.uiFlags,
  isPipelineEnabled: $state => $state.isPipelineEnabled,
  getPipelineById: $state => id => {
    return $state.records.find(pipeline => String(pipeline.id) === String(id));
  },
};

const actions = {
  async fetch({ commit, dispatch, state: $state }, { force = false } = {}) {
    // Skip if already fetching, or already loaded (unless force refresh)
    if ($state.uiFlags.isFetching) return $state.records;
    if (!force && $state.records.length > 0) return $state.records;
    commit(types.SET_PIPELINE_UI_FLAG, { isFetching: true });
    try {
      const { data } = await PipelineAPI.get();
      const formattedData = data.map(pipeline => ({
        ...pipeline,
        id: String(pipeline.id),
        stages: Object.entries(pipeline.stages || {}).reduce(
          (acc, [key, stage]) => ({
            ...acc,
            [String(key)]: {
              ...stage,
              position: parseInt(stage.position, 10) || 0,
            },
          }),
          {}
        ),
      }));
      commit(types.SET_PIPELINES, formattedData);

      // Recupera o ID do pipeline saved no localStorage
      // const savedPipelineId = localStorage.getItem(STORAGE_KEY);

      // Se não houver pipeline saved ou ele não existir mais, seleciona o primeiro
      if (!$state.selectedPipeline && formattedData.length > 0) {
        await dispatch('setSelectedPipeline', formattedData[0]);
      }

      return formattedData;
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      return handlePipelineError(error);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetching: false });
    }
  },

  async setSelectedPipeline({ commit }, pipeline) {
    if (!pipeline?.id) {
      console.error('Attempt to select invalid pipeline:', pipeline);
      return;
    }

    try {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: true });

      // Salva o ID do pipeline in localStorage
      // localStorage.setItem(STORAGE_KEY, String(pipeline.id));

      commit(types.SET_SELECTED_PIPELINE, pipeline);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: false });
    }
  },

  setPipelineEnabled({ commit }, enabled) {
    commit(types.SET_PIPELINE_ENABLED, enabled);
  },

  async createPipeline({ commit }, payload) {
    commit(types.SET_PIPELINE_UI_FLAG, { isCreating: true });
    try {
      const { data } = await PipelineAPI.create(payload);
      commit('ADD_PIPELINE_REMOTE', data);
      return data;
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isCreating: false });
    }
  },

  async updatePipeline({ commit }, { id, ...payload }) {
    commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await PipelineAPI.update(id, payload);
      // UPDATE_PIPELINE_REMOTE already updates selectedPipeline if IDs match
      commit('UPDATE_PIPELINE_REMOTE', data);
      return data;
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: false });
    }
  },

  async deletePipeline({ commit }, pipelineId) {
    await PipelineAPI.delete(pipelineId);
    commit('REMOVE_PIPELINE_REMOTE', pipelineId);
  },
};

const mutations = {
  [types.SET_PIPELINES]($state, data) {
    $state.records = data;
  },
  [types.SET_SELECTED_PIPELINE]($state, pipeline) {
    $state.selectedPipeline = pipeline;
  },
  [types.SET_PIPELINE_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_PIPELINE_ENABLED]($state, enabled) {
    $state.isPipelineEnabled = enabled;
  },

  // === REAL-TIME MUTATIONS (from WebSocket) ===

  // Add pipeline from remote (other user created)
  ADD_PIPELINE_REMOTE($state, pipeline) {
    const exists = $state.records.find(
      p => String(p.id) === String(pipeline.id)
    );
    if (!exists && pipeline.id) {
      const formattedPipeline = {
        ...pipeline,
        id: String(pipeline.id),
        stages: Object.entries(pipeline.stages || {}).reduce(
          (acc, [key, stage]) => ({
            ...acc,
            [String(key)]: {
              ...stage,
              position: parseInt(stage.position, 10) || 0,
            },
          }),
          {}
        ),
      };
      $state.records.push(formattedPipeline);
    }
  },

  // Update pipeline from remote (other user updated)
  UPDATE_PIPELINE_REMOTE($state, pipeline) {
    const index = $state.records.findIndex(
      p => String(p.id) === String(pipeline.id)
    );
    if (index !== -1) {
      const formattedPipeline = {
        ...$state.records[index],
        ...pipeline,
        id: String(pipeline.id),
        stages: pipeline.stages
          ? Object.entries(pipeline.stages).reduce(
              (acc, [key, stage]) => ({
                ...acc,
                [String(key)]: {
                  ...stage,
                  position: parseInt(stage.position, 10) || 0,
                },
              }),
              {}
            )
          : $state.records[index].stages,
      };
      $state.records.splice(index, 1, formattedPipeline);

      // Update selectedPipeline if it's the same
      if (
        $state.selectedPipeline &&
        String($state.selectedPipeline.id) === String(pipeline.id)
      ) {
        $state.selectedPipeline = formattedPipeline;
      }
    }
  },

  // Remove pipeline from remote (other user deleted)
  REMOVE_PIPELINE_REMOTE($state, pipelineId) {
    $state.records = $state.records.filter(
      p => String(p.id) !== String(pipelineId)
    );

    // If deleted pipeline was selected, clear selection
    if (
      $state.selectedPipeline &&
      String($state.selectedPipeline.id) === String(pipelineId)
    ) {
      $state.selectedPipeline =
        $state.records.length > 0 ? $state.records[0] : null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
